import axios, { AxiosResponse } from 'axios';
import { AuthResponse, LoginRequest, Course, Category, CreateCourseData, CreateCategoryData } from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.escuela.agentesia.cloud/api/v1';

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 1200000, // 20 minutes timeout for all requests
});

// Setup request interceptor

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  // Get token from localStorage first
  const token = localStorage.getItem('token');
  
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  console.error('❌ Request interceptor error:', error);
  return Promise.reject(error);
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (data: LoginRequest): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/login', data),
  
  loginWith2FA: (data: { tempToken: string; twoFactorCode: string }): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/login/2fa', data),
  
  getProfile: (): Promise<AxiosResponse<any>> =>
    api.get('/auth/profile'),
  
  updateProfile: (data: { firstName?: string; lastName?: string; avatar?: string }): Promise<AxiosResponse<any>> =>
    api.put('/auth/profile', data),
  
  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<AxiosResponse<any>> =>
    api.post('/auth/change-password', data),
  
  enable2FA: (): Promise<AxiosResponse<any>> =>
    api.post('/auth/enable-2fa'),
  
  verify2FA: (data: { token: string }): Promise<AxiosResponse<any>> =>
    api.post('/auth/verify-2fa', data),
  
  disable2FA: (data: { password: string; token?: string }): Promise<AxiosResponse<any>> =>
    api.post('/auth/disable-2fa', data),
  
  logout: (): Promise<AxiosResponse<any>> =>
    api.post('/auth/logout'),
};

// Categories API
export const categoriesApi = {
  getAll: (): Promise<AxiosResponse<Category[]>> =>
    api.get('/categories'),
  
  getById: (id: string): Promise<AxiosResponse<Category>> =>
    api.get(`/categories/${id}`),
  
  create: (data: CreateCategoryData): Promise<AxiosResponse<Category>> =>
    api.post('/categories', data),
  
  update: (id: string, data: Partial<CreateCategoryData>): Promise<AxiosResponse<Category>> =>
    api.put(`/categories/${id}`, data),
  
  delete: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/categories/${id}`),
};

// Courses API
export const coursesApi = {
  getAll: (params?: {
    categoryId?: string;
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<AxiosResponse<any>> =>
    api.get('/courses', { params }),
  
  getById: (id: string): Promise<AxiosResponse<Course>> =>
    api.get(`/courses/${id}`),
  
  create: (data: CreateCourseData): Promise<AxiosResponse<Course>> =>
    api.post('/courses', data),
  
  update: (id: string, data: Partial<CreateCourseData>): Promise<AxiosResponse<Course>> =>
    api.put(`/courses/${id}`, data),
  
  updateStatus: (id: string, status: 'PUBLISHED' | 'DRAFT' | 'ARCHIVED'): Promise<AxiosResponse<Course>> =>
    api.patch(`/courses/${id}/status`, { status }),
  
  delete: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/courses/${id}`),
  
  getFeatured: (): Promise<AxiosResponse<Course[]>> =>
    api.get('/courses/featured'),
};

// Students API
export const studentsApi = {
  getAll: (params?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<any>> =>
    api.get('/students', { params }),
  
  getById: (id: string): Promise<AxiosResponse<any>> =>
    api.get(`/students/${id}`),
  
  getStats: (): Promise<AxiosResponse<any>> =>
    api.get('/students/stats'),
  
  updateStatus: (id: string, isActive: boolean): Promise<AxiosResponse<any>> =>
    api.patch(`/students/${id}/status`, { isActive }),
  
  update: (id: string, data: { firstName?: string; lastName?: string; email?: string }): Promise<AxiosResponse<any>> =>
    api.put(`/students/${id}`, data),
  
  delete: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/students/${id}`),
};

// Users/Admin API (for managing all types of users)
export const usersApi = {
  getAllUsers: (params?: {
    role?: 'INSTRUCTOR' | 'STUDENT' | 'ADMIN';
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<any>> =>
    api.get('/users', { params }),
  
  getUserById: (id: string): Promise<AxiosResponse<any>> =>
    api.get(`/users/${id}`),
  
  createUser: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: 'INSTRUCTOR' | 'STUDENT' | 'ADMIN';
    isActive?: boolean;
  }): Promise<AxiosResponse<any>> =>
    api.post('/users', data),
  
  updateUser: (id: string, data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: 'INSTRUCTOR' | 'STUDENT' | 'ADMIN';
    isActive?: boolean;
  }): Promise<AxiosResponse<any>> =>
    api.put(`/users/${id}`, data),
  
  deleteUser: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/users/${id}`),
  
  updateUserStatus: (id: string, isActive: boolean): Promise<AxiosResponse<any>> =>
    api.patch(`/users/${id}/status`, { isActive }),
  
  resetPassword: (id: string, newPassword: string): Promise<AxiosResponse<any>> =>
    api.post(`/users/${id}/reset-password`, { newPassword }),
};

// Instructors API
export const instructorsApi = {
  getAll: (params?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<any>> =>
    api.get('/instructors', { params }),
  
  getById: (id: string): Promise<AxiosResponse<any>> =>
    api.get(`/instructors/${id}`),
  
  create: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    isActive?: boolean;
  }): Promise<AxiosResponse<any>> =>
    api.post('/instructors', data),
  
  update: (id: string, data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    isActive?: boolean;
  }): Promise<AxiosResponse<any>> =>
    api.put(`/instructors/${id}`, data),
  
  delete: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/instructors/${id}`),
  
  updateStatus: (id: string, isActive: boolean): Promise<AxiosResponse<any>> =>
    api.patch(`/instructors/${id}/status`, { isActive }),
  
  getStats: (): Promise<AxiosResponse<any>> =>
    api.get('/instructors/stats'),
};

// Modules API  
export const modulesApi = {
  getByCourse: (courseId: string): Promise<AxiosResponse<any>> =>
    api.get(`/modules/course/${courseId}`),
  
  getById: (id: string): Promise<AxiosResponse<any>> =>
    api.get(`/modules/${id}`),
  
  create: (courseId: string, formData: FormData): Promise<AxiosResponse<any>> => {
    // Check if this is a large video upload that should use chunk upload
    const videoFile = formData.get('video') as File;
    if (videoFile && videoFile.size > 50 * 1024 * 1024) { // 50MB threshold - use chunk upload for files larger than 50MB
      // For large files, use chunk upload
      return new Promise((resolve, reject) => {
        const title = formData.get('title') as string;
        const description = formData.get('description') as string;
        const videoDuration = formData.get('videoDuration') as string;

        import('@/services/chunkUpload').then(({ chunkUploadService }) => {
          chunkUploadService.uploadFile({
            file: videoFile,
            courseId,
            title,
            description,
            videoDuration: videoDuration ? parseInt(videoDuration) : 0,
            chunkSize: 20 * 1024 * 1024, // 20MB chunks
            onProgress: (progress) => {
              console.log(`Upload progress: ${progress}%`);
            },
            onComplete: (response) => {
              resolve({ data: response } as AxiosResponse<any>);
            },
            onError: (error) => {
              reject(error);
            }
          });
        }).catch(reject);
      });
    } else {
      // For small files, use regular upload
      return api.post(`/modules/course/${courseId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 1200000 // 20 minutes timeout for large video uploads
      });
    }
  },
  
  update: (id: string, formData: FormData): Promise<AxiosResponse<any>> =>
    api.put(`/modules/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 1200000 // 20 minutes timeout for large video uploads
    }),
  
  delete: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/modules/${id}`),
  
  toggleStatus: (id: string): Promise<AxiosResponse<any>> =>
    api.patch(`/modules/${id}/toggle-status`),
  
  reorder: (courseId: string, moduleIds: string[]): Promise<AxiosResponse<any>> =>
    api.put(`/modules/course/${courseId}/reorder`, { moduleIds }),
};

// Upload API
export const uploadApi = {
  uploadContent: (formData: FormData): Promise<AxiosResponse<any>> =>
    api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 1200000 // 20 minutes timeout for large file uploads
    }),

  getContent: (courseId?: string): Promise<AxiosResponse<any>> =>
    api.get('/upload', { params: courseId ? { courseId } : {} }),

  getGalleryContent: (courseId?: string): Promise<AxiosResponse<any>> =>
    api.get('/upload/gallery-v2', { params: courseId ? { courseId } : {} }),

  deleteContent: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/upload/${id}`),

  deleteFile: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/upload/file/${id}`),

  getStats: (): Promise<AxiosResponse<any>> =>
    api.get('/upload/stats'),
};

// Analytics API
export const analyticsApi = {
  getMetrics: (): Promise<AxiosResponse<any>> =>
    api.get('/analytics/metrics'),
  
  getChartData: (period?: string): Promise<AxiosResponse<any>> =>
    api.get('/analytics/chart-data', { params: period ? { period } : {} }),
  
  getTopCourses: (limit?: number): Promise<AxiosResponse<any>> =>
    api.get('/analytics/top-courses', { params: limit ? { limit } : {} }),
  
  getUserActivity: (): Promise<AxiosResponse<any>> =>
    api.get('/analytics/user-activity'),
  
  getRevenueBreakdown: (): Promise<AxiosResponse<any>> =>
    api.get('/analytics/revenue-breakdown'),
  
  getDetailedReport: (params: { startDate: string; endDate: string }): Promise<AxiosResponse<any>> =>
    api.get('/analytics/detailed-report', { params }),
  
  generateReport: (data: {
    startDate: string;
    endDate: string;
    format: 'pdf' | 'excel' | 'csv';
    csvType?: 'students' | 'courses' | 'revenue' | 'enrollments';
    type?: 'general' | 'students' | 'courses' | 'revenue' | 'enrollments';
  }): Promise<AxiosResponse<any>> =>
    api.post('/analytics/generate-report', data),
  
  downloadReport: (fileName: string): Promise<AxiosResponse<any>> =>
    api.get(`/analytics/download/${fileName}`, { responseType: 'blob' }),
  
  // Security-related endpoints
  createBackup: (format: string): Promise<AxiosResponse<any>> =>
    api.post('/analytics/backup', { format }),
  
  cleanupFiles: (data: { type: string }): Promise<AxiosResponse<any>> =>
    api.post('/analytics/cleanup', data),
  
  getSystemStats: (): Promise<AxiosResponse<any>> =>
    api.get('/analytics/system-stats'),
};

// Exercises API
export const exercisesApi = {
  getAll: (params?: {
    courseId?: string;
  }): Promise<AxiosResponse<any>> =>
    api.get('/exercises', { params }),
  
  getById: (id: string): Promise<AxiosResponse<any>> =>
    api.get(`/exercises/${id}`),
  
  create: (data: {
    title: string;
    description?: string;
    courseId: string;
    order?: number;
    questions: any[];
  }): Promise<AxiosResponse<any>> =>
    api.post('/exercises', data),
  
  update: (id: string, data: {
    title?: string;
    description?: string;
    courseId?: string;
    order?: number;
    questions?: any[];
  }): Promise<AxiosResponse<any>> =>
    api.put(`/exercises/${id}`, data),
  
  delete: (id: string): Promise<AxiosResponse<any>> =>
    api.delete(`/exercises/${id}`),
  
  getStats: (): Promise<AxiosResponse<any>> =>
    api.get('/exercises/stats'),
  
  submitAnswer: (id: string, answers: any): Promise<AxiosResponse<any>> =>
    api.post(`/exercises/${id}/submit`, answers),
  
  getSubmissions: (id: string): Promise<AxiosResponse<any>> =>
    api.get(`/exercises/${id}/submissions`),
};

// Health check
export const healthApi = {
  check: (): Promise<AxiosResponse<any>> =>
    api.get('/health'),
};